import "regenerator-runtime/runtime";
import * as Sentry from "@sentry/browser";
import {
  RelayNetworkLayer,
  urlMiddleware,
  loggerMiddleware,
  errorMiddleware,
  perfMiddleware,
  retryMiddleware,
  authMiddleware,
  type Middleware,
  type SubscribeFunction as RelayNetworkLayerSubscribeFunction,
} from "react-relay-network-modern";
import { Environment, RecordSource, Store } from "relay-runtime";

import { getAuthToken } from "app/utils/auth";
import { getConfigs, type Configs } from "app/utils/configs";
import { subscribeFn } from "app/utils/subscriptionClient";

const configsByRequest = new WeakMap<object, Configs>();

/**
 * Resolves the GraphQL endpoint, and keeps the configs for the rest of the
 * request.
 *
 * Two middlewares in this chain need the configs, and they run one after the
 * other, so a shared read needs somewhere to live for the length of one request.
 * Without this, every operation reads the config files twice.
 *
 * Exported for tests.
 */
export async function resolveGqlUri(req: object): Promise<string> {
  const config = await getConfigs();
  configsByRequest.set(req, config);
  return config.api.url;
}

/**
 * Adds the deployment's custom headers to a request.
 *
 * Reuses the configs that `resolveGqlUri` read for this request, and reads them
 * itself if it runs without it.
 *
 * Exported for tests.
 */
export const deploymentHeadersMiddleware: Middleware = (next) => {
  return async (req) => {
    const config = configsByRequest.get(req) ?? (await getConfigs());

    if (config.api.headers) {
      try {
        req.fetchOpts.headers = {
          ...req.fetchOpts.headers,
          ...config.api.headers,
        };
      } catch {
        console.error(
          "Custom headers appear to be malformed, please check deployment.json",
        );
      }
    }
    return next(req);
  };
};

const hasGqlDebug = import.meta.env.VITE_GQL_DEBUG?.toLowerCase() === "true";
const __DEV__ = import.meta.env.NODE_ENV !== "production" && hasGqlDebug;

const network = new RelayNetworkLayer(
  [
    __DEV__ ? loggerMiddleware() : null,
    __DEV__ ? perfMiddleware() : null,
    urlMiddleware({ url: resolveGqlUri }),
    retryMiddleware({
      beforeRetry: ({ abort, req }) => {
        // resource matches can experience query perform issues
        // so we avoid retrying.
        if (req.getID().includes("ResourceMatches")) {
          abort("Aborting retry for resource matches");
        }
      },
      fetchTimeout: 15000,
      retryDelays: [3200, 6400, 12800, 25600, 51200],
      statusCodes: [500, 502, 503, 504],
    }),
    authMiddleware({
      token: () => getAuthToken(),
    }),
    errorMiddleware({
      logger: (message: string) => {
        Sentry.captureMessage(message);
      },
    }),
    deploymentHeadersMiddleware,
  ],
  {
    noThrow: true,
    // react-relay-network-modern's hand-written types predate modern
    // relay-runtime: its SubscribeFunction takes a `ConcreteBatch` and it
    // declares `RelayObservable<T> = Promise<T>`, neither of which matches
    // what it actually passes through to relay-runtime at runtime (a plain
    // RequestParameters, and a real Observable). Keep subscribeFn typed
    // against relay-runtime, which is the contract that's really in force,
    // and absorb the library's stale types here.
    subscribeFn: subscribeFn as unknown as RelayNetworkLayerSubscribeFunction,
  },
);

const store = new Store(new RecordSource());

const RelayEnvironment = new Environment({ network, store });

export { RelayEnvironment };
